// src/pages/Tools/nintendo-quest/NextChallenges.tsx

import React from 'react';
import { motion } from 'framer-motion';
import { Target, Clock, ArrowRight, Zap } from 'lucide-react';
import { NextChallenge } from '../../../lib/nintendo-quest/types';

interface NextChallengesProps {
  challenges: NextChallenge[];
}

const priorityConfig = {
  high: {
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-400/30',
    textColor: 'text-red-300',
    iconColor: 'text-red-400'
  },
  medium: {
    bgColor: 'bg-orange-500/20',
    borderColor: 'border-orange-400/30',
    textColor: 'text-orange-300',
    iconColor: 'text-orange-400'
  },
  low: {
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-400/30',
    textColor: 'text-blue-300',
    iconColor: 'text-blue-400'
  },
  aspirational: {
    bgColor: 'bg-purple-500/20',
    borderColor: 'border-purple-400/30',
    textColor: 'text-purple-300',
    iconColor: 'text-purple-400'
  }
};

const typeConfig = {
  points: { label: 'Points Goal', icon: Target },
  streak: { label: 'Streak Goal', icon: Zap },
  milestone: { label: 'Milestone', icon: Target },
  achievement: { label: 'Achievement', icon: Target }
};

export default function NextChallenges({ challenges }: NextChallengesProps) {
  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <Target className="w-6 h-6 text-red-400" />
          Next Challenges
        </h3>
        <div className="text-sm text-white/60">
          What's next?
        </div>
      </div>

      {/* Challenge List */}
      <div className="space-y-4">
        {challenges.map((challenge, index) => {
          const priority = priorityConfig[challenge.priority];
          const typeInfo = typeConfig[challenge.type];
          const TypeIcon = typeInfo.icon;
          
          return (
            <motion.div
              key={`${challenge.title}-${index}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`${priority.bgColor} ${priority.borderColor} border rounded-lg p-4 hover:scale-[1.02] transition-transform duration-200`}
            >
              {/* Challenge Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="text-2xl">{challenge.icon}</div>
                  <div className="flex-1">
                    <div className="text-white font-bold text-sm">
                      {challenge.title}
                    </div>
                    <div className={`text-xs ${priority.textColor}`}>
                      {typeInfo.label}
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-xl font-bold text-white">
                    {typeof challenge.value === 'number' && challenge.value > 100 
                      ? challenge.value.toLocaleString() 
                      : challenge.value}
                  </div>
                  <div className="text-xs text-white/60">
                    {challenge.type === 'points' ? 'points' : 
                     challenge.type === 'streak' ? 'days' : 
                     challenge.type === 'milestone' ? 'shlokas' : 'goal'}
                  </div>
                </div>
              </div>

              {/* Challenge Details */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4">
                  {challenge.estimatedDays && (
                    <div className="flex items-center gap-1 text-white/60">
                      <Clock className="w-3 h-3" />
                      <span>{challenge.estimatedDays} days</span>
                    </div>
                  )}
                  
                  {challenge.category && (
                    <div className={`px-2 py-1 rounded-full text-xs ${priority.bgColor} ${priority.textColor}`}>
                      {challenge.category}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-1 text-white/60">
                  <span className="text-xs">{challenge.priority} priority</span>
                  <ArrowRight className="w-3 h-3" />
                </div>
              </div>

              {/* Progress indicator for points-based challenges */}
              {challenge.type === 'points' && typeof challenge.value === 'number' && challenge.value > 0 && (
                <div className="mt-3">
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div 
                      className={`h-full ${priority.iconColor.replace('text-', 'bg-').replace('-400', '-500')} rounded-full transition-all duration-300`}
                      style={{ 
                        width: `${Math.max(5, Math.min(95, (1 - (challenge.value / 100)) * 100))}%` 
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Description if available */}
              {challenge.description && (
                <div className="mt-2 text-xs text-white/70">
                  {challenge.description}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Empty state */}
      {challenges.length === 0 && (
        <div className="text-center py-8">
          <Target className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <div className="text-gray-300">No challenges available</div>
          <div className="text-gray-400 text-sm">You're doing great! Keep it up!</div>
        </div>
      )}
    </div>
  );
}